import React, { useEffect, useState, useRef } from "react";
import { TID } from "@/constants/testIds";
import { COPY } from "@/copy";
import { fetchFups } from "@/lib/api";

const INTENTS = ["all", "hookup", "situationship", "serious", "ambiguous", "avoidance", "validation"];

const intentLabel = (i) => {
  const map = {
    all: COPY.intent_all, hookup: COPY.intent_hookup, situationship: COPY.intent_situationship,
    serious: COPY.intent_serious, ambiguous: COPY.intent_ambiguous, avoidance: COPY.intent_avoidance, validation: COPY.intent_validation,
  };
  return map[i] || i;
};

export const FupsRepository = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("all");
  const [intent, setIntent] = useState("all");
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  const load = (params) => {
    setLoading(true);
    fetchFups(params)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  // Initial load — gets 12 core + 12 randomly sampled.
  useEffect(() => {
    load({});
  }, []);

  // Re-fetch when filters change (debounced for search).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const params = {};
      if (gender !== "all") params.gender = gender;
      if (intent !== "all") params.intent = intent;
      if (search.trim()) params.search = search.trim();
      load(params);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [search, gender, intent]);

  const intentColor = (i) => {
    switch (i) {
      case "hookup": return "bg-ds-flame text-ds-ink";
      case "situationship": return "bg-ds-yellow text-ds-ink";
      case "serious": return "bg-emerald-400 text-ds-ink";
      case "avoidance": return "bg-zinc-500 text-ds-bone";
      case "validation": return "bg-fuchsia-400 text-ds-ink";
      default: return "bg-ds-paper text-ds-ink";
    }
  };

  return (
    <section
      id="fups"
      data-testid={TID.fupsSection}
      className="bg-ds-paper ds-border border-l-0 border-r-0 border-t-0"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="ds-mono text-xs uppercase tracking-[0.3em] text-ds-flame mb-4">
          {COPY.section_fups}
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-3xl">
            <h2 className="ds-display text-4xl md:text-6xl">{COPY.fups_title}</h2>
            <p className="mt-4 text-ds-ink/70 text-base md:text-lg">{COPY.fups_sub}</p>
          </div>
          <div data-testid={TID.fupsCount} className="ds-mono text-xs uppercase tracking-widest text-ds-ink/50">
            {COPY.counted(items.length)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-10">
          <div className="md:col-span-5">
            <input
              data-testid={TID.fupsSearch}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={COPY.fups_search}
              className="w-full ds-border bg-ds-bone px-4 py-3 ds-mono text-sm focus:outline-none focus:ring-2 focus:ring-ds-flame"
            />
          </div>
          <div className="md:col-span-4 flex ds-border w-full">
            <FilterBtn dt={TID.fupsGenderAll} active={gender === "all"} onClick={() => setGender("all")}>{COPY.fups_all}</FilterBtn>
            <Sep />
            <FilterBtn dt={TID.fupsGenderMale} active={gender === "male"} onClick={() => setGender("male")}>{COPY.fups_men}</FilterBtn>
            <Sep />
            <FilterBtn dt={TID.fupsGenderFemale} active={gender === "female"} onClick={() => setGender("female")}>{COPY.fups_women}</FilterBtn>
          </div>
          <div className="md:col-span-3">
            <select
              data-testid={TID.fupsIntent}
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              className="w-full ds-border bg-ds-bone px-3 py-3 ds-mono text-xs uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-ds-flame"
            >
              {INTENTS.map((i) => (
                <option key={i} value={i}>{intentLabel(i)}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="ds-mono text-xs uppercase tracking-widest text-ds-ink/50">loading bank…</div>
        ) : items.length === 0 ? (
          <div className="ds-border bg-ds-bone p-10 text-center ds-display text-2xl text-ds-ink/40">
            no phrases match. try a wider net.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <article
                key={it.id}
                data-testid={TID.fupsCard(it.id)}
                className="ds-border bg-ds-bone p-6 ds-shadow hover:ds-shadow-lg hover:-translate-y-1 transition-all flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`ds-mono text-[10px] uppercase tracking-widest px-2 py-0.5 ds-border ${intentColor(it.intent)}`}>
                    {it.intent}
                  </span>
                  <span className="ds-mono text-[10px] uppercase tracking-widest text-ds-ink/50">
                    {it.gender === "male" ? "♂ men" : it.gender === "female" ? "♀ women" : "any"}
                  </span>
                </div>
                <h3 className="ds-display text-2xl leading-tight mb-1">
                  &quot;{it.phrase}&quot;
                </h3>
                <div className="ds-mono text-[10px] uppercase tracking-widest text-ds-ink/40 mt-3 mb-1">
                  {COPY.fups_meaning}
                </div>
                <p className="text-ds-ink/80 leading-relaxed flex-1">
                  {it.meaning}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <span className="ds-mono text-[10px] uppercase tracking-widest text-ds-ink/40">{COPY.fups_spice}</span>
                  <span className="ds-mono text-xs tracking-wider text-ds-flame">
                    {"●".repeat(it.spice)}<span className="text-ds-ink/20">{"●".repeat(5 - it.spice)}</span>
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const FilterBtn = ({ children, active, onClick, dt }) => (
  <button
    data-testid={dt}
    onClick={onClick}
    className={`flex-1 px-3 py-3 ds-mono text-xs uppercase tracking-widest transition-colors ${active ? "bg-ds-ink text-ds-bone" : "bg-ds-bone hover:bg-ds-ink hover:text-ds-bone"}`}
  >
    {children}
  </button>
);
const Sep = () => <div className="w-px bg-ds-ink" />;
